import React, { useCallback } from 'react';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { segmentTrack } from 'flow/actions/segment-actions';
import { useModal } from 'flow/contexts/ModalProvider';
import { MessageContent } from 'flow/components/tickets/UseAttachmentHandler';
import { TicketKey } from 'flow/types/EntityKeys';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';
import {
    getFileExtensionWithDot,
    sendInternalNote,
    sendSMSMessage,
    onSendWhatsAppMessageRequested,
    onSendEmailRequested,
} from '../InputActions';
import AttachmentUploadError from 'flow/components/tickets/Input/Attachments/AttachmentUploadError';
import {
    ChannelValues,
    CHANNEL,
} from 'flow/components/tickets/messageChannels';
import useTicketAction from 'flow/hooks/useTicketAction';

export type MessageChannel = 'internal' | ChannelValues;

const SEGMENT_EVENTS_BY_CHANNEL: Record<MessageChannel, string | null> = {
    internal: TICKET_STRINGS.INTERNAL_NOTE_PUBLISHED,
    sms: TICKET_STRINGS.SMS_MESSAGE_PUBLISHED,
    whatsapp: TICKET_STRINGS.WHATSAPP_MESSAGE_PUBLISHED,
    email: TICKET_STRINGS.EMAIL_MESSAGE_PUBLISHED,
};

interface UseSendTicketMessageParams {
    ticketKey: TicketKey;
    tabSelectedIsWritable: boolean;
    messageContentInternal: MessageContent;
    messageContentReplyExternal: MessageContent;
    setMessageContentInternal: React.Dispatch<
        React.SetStateAction<MessageContent>
    >;
    setMessageContentReplyExternal: React.Dispatch<
        React.SetStateAction<MessageContent>
    >;
    resetSegmentTrackingFlags: (channel: MessageChannel) => void;
}

interface UseSendTicketMessageReturn {
    handleSendMessage: (channel: MessageChannel) => Promise<void>;
}

export const useSendTicketMessage = ({
    ticketKey,
    tabSelectedIsWritable,
    messageContentInternal,
    messageContentReplyExternal,
    setMessageContentInternal,
    setMessageContentReplyExternal,
    resetSegmentTrackingFlags,
}: UseSendTicketMessageParams): UseSendTicketMessageReturn => {
    const dispatch = useAppDispatch();
    const runTicketAction = useTicketAction();
    const { openModal } = useModal();

    const clearMessageContent = useCallback(
        (channel: MessageChannel) => {
            const emptyContent: MessageContent = {
                text: '',
                attachment: null,
                imageSrc: null,
            };

            if (channel === 'internal') {
                setMessageContentInternal(emptyContent);
            } else {
                setMessageContentReplyExternal(emptyContent);
            }
        },
        [setMessageContentInternal, setMessageContentReplyExternal],
    );

    const trackMessageSent = useCallback(
        (channel: MessageChannel) => {
            const segmentEvent = SEGMENT_EVENTS_BY_CHANNEL[channel];
            if (segmentEvent) {
                dispatch(
                    segmentTrack(segmentEvent, {
                        component: 'Ticket input field',
                    }),
                );
            }
        },
        [dispatch],
    );

    const onErrorSending = useCallback(
        (
            error: unknown,
            channel: MessageChannel,
            attachment: MessageContent['attachment'],
        ) => {
            if (error instanceof AttachmentUploadError) {
                openModal('ticketAttachmentNotAllowed', {
                    channel,
                    fileExtensionUserFriendly: getFileExtensionWithDot(
                        attachment?.file?.name,
                    ),
                    fileMimeType: attachment?.file?.type,
                    reason: error.code,
                });
            }
        },
        [openModal],
    );

    const getSendAction = useCallback(
        (
            channel: MessageChannel,
            text: string,
            attachment: MessageContent['attachment'],
            emailSignature?: string,
            mentions?: MessageContent['mentions'],
        ) => {
            switch (channel) {
                case 'internal':
                    return sendInternalNote(
                        ticketKey,
                        text,
                        attachment,
                        mentions,
                    );
                case CHANNEL.SMS:
                    return sendSMSMessage(ticketKey, text, attachment);
                case CHANNEL.WHATSAPP:
                    return onSendWhatsAppMessageRequested(
                        ticketKey,
                        text,
                        attachment,
                    );
                case CHANNEL.EMAIL:
                    return onSendEmailRequested(
                        ticketKey,
                        text,
                        attachment,
                        emailSignature,
                    );
                default:
                    throw new Error(`Unknown channel: ${channel}`);
            }
        },
        [ticketKey],
    );

    const handleSendMessage = useCallback(
        async (channel: MessageChannel) => {
            // The ticket's reply / addInternalNote permission, per the tab in effect.
            if (!tabSelectedIsWritable) {
                return;
            }

            const messageContent =
                channel === 'internal'
                    ? messageContentInternal
                    : messageContentReplyExternal;

            const textWithoutTrailingWhitespaces =
                messageContent.text.trimEnd();
            const attachment = messageContent.attachment;
            const emailSignature = messageContent.emailSignature;
            const mentions = messageContent.mentions;

            // Clear the message content immediately
            clearMessageContent(channel);

            // Reset segment tracking state
            resetSegmentTrackingFlags(channel);

            try {
                const action = getSendAction(
                    channel,
                    textWithoutTrailingWhitespaces,
                    attachment,
                    emailSignature,
                    mentions,
                );

                // Awaited on every channel, internal notes included: a send now
                // reports a refusal by rejecting, and nothing would hear it.
                await runTicketAction(action);

                trackMessageSent(channel);
            } catch (error) {
                onErrorSending(error, channel, attachment);
            }
        },
        [
            runTicketAction,
            tabSelectedIsWritable,
            messageContentInternal,
            messageContentReplyExternal,
            clearMessageContent,
            resetSegmentTrackingFlags,
            getSendAction,
            dispatch,
            trackMessageSent,
            onErrorSending,
        ],
    );

    return {
        handleSendMessage,
    };
};
